import { Fragment } from 'react';
import { Link } from 'react-router-dom';

/** Inline: bold (**text**), italic (*text*), numbers/$ highlighted */
function InlineText({ text }: { text: string }) {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|\*[^*]+\*|\$[\d,]+(?:\.\d+)?|\b\d+(?:,\d{3})*(?:\.\d+)?%?\b)/g);
  return (
    <>
      {parts.map((part, i) => {
        const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (linkMatch) {
          const href = linkMatch[2];
          const label = linkMatch[1];
          const className =
            'font-medium text-blue-600 hover:underline underline-offset-2';
          // Internal app links — keep navigation inside the SPA
          if (href.startsWith('/')) {
            return (
              <Link key={i} to={href} className={className}>
                {label}
              </Link>
            );
          }
          return (
            <a
              key={i}
              href={href}
              target="_blank"
              rel="noreferrer"
              className={className}
            >
              {label}
            </a>
          );
        }
        if (part.startsWith('**') && part.endsWith('**'))
          return <strong key={i} className="font-semibold text-slate-900">{part.slice(2, -2)}</strong>;
        if (part.startsWith('*') && part.endsWith('*'))
          return <em key={i} className="italic">{part.slice(1, -1)}</em>;
        if (/^\$[\d,]+/.test(part) || /^\d+(?:,\d{3})*(?:\.\d+)?%?$/.test(part))
          return <span key={i} className="font-semibold text-indigo-600">{part}</span>;
        return <Fragment key={i}>{part}</Fragment>;
      })}
    </>
  );
}

/** Renders a full AI markdown response into styled React elements */
export function MarkdownContent({ content }: { content: string }) {
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let listItems: string[] = [];
  let orderedItems: string[] = [];
  let key = 0;

  const flushList = () => {
    if (listItems.length) {
      elements.push(
        <ul key={key++} className="space-y-1 my-1">
          {listItems.map((item, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-indigo-400 shrink-0" />
              <span className="text-slate-700 leading-snug"><InlineText text={item} /></span>
            </li>
          ))}
        </ul>,
      );
      listItems = [];
    }
    if (orderedItems.length) {
      elements.push(
        <ol key={key++} className="space-y-1 my-1">
          {orderedItems.map((item, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-indigo-500 font-semibold text-xs mt-0.5 w-4 shrink-0">{i + 1}.</span>
              <span className="text-slate-700 leading-snug"><InlineText text={item} /></span>
            </li>
          ))}
        </ol>,
      );
      orderedItems = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();

    if (!line) {
      flushList();
      continue;
    }

    // Section headers ## or ###
    if (/^#{1,3}\s/.test(line)) {
      flushList();
      const text = line.replace(/^#+\s/, '');
      elements.push(
        <p key={key++} className="text-xs font-semibold uppercase tracking-wider text-indigo-600 mt-3 mb-1">
          <InlineText text={text} />
        </p>,
      );
      continue;
    }

    // Unordered list
    const bulletMatch = line.match(/^[-*•]\s+(.*)/);
    if (bulletMatch) {
      if (orderedItems.length) flushList();
      listItems.push(bulletMatch[1]);
      continue;
    }

    // Ordered list
    const orderedMatch = line.match(/^\d+\.\s+(.*)/);
    if (orderedMatch) {
      if (listItems.length) flushList();
      orderedItems.push(orderedMatch[1]);
      continue;
    }

    // Key: Value pattern → stat chip row
    const kvMatch = line.match(/^(\*{0,2})([^:]+?)\*{0,2}:\s+(.+)$/);
    if (kvMatch && kvMatch[2].length < 30 && !kvMatch[2].includes('.')) {
      flushList();
      elements.push(
        <div key={key++} className="flex items-baseline gap-1.5 py-0.5">
          <span className="text-xs font-medium text-slate-500 shrink-0">{kvMatch[2].replace(/\*/g, '')}:</span>
          <span className="text-sm font-medium text-slate-800"><InlineText text={kvMatch[3]} /></span>
        </div>,
      );
      continue;
    }

    // Regular paragraph
    flushList();
    elements.push(
      <p key={key++} className="text-sm text-slate-700 leading-relaxed">
        <InlineText text={line} />
      </p>,
    );
  }

  flushList();
  return <div className="space-y-0.5">{elements}</div>;
}
